import { Stack, useBreakpointValue } from "@chakra-ui/react";
import { SectionContentLayout } from "../../../components/utils";
import type { DealFinderTicker } from "../../../../../api/tickers/models/DealFinderTicker";
import { useCollectionKey, useDealFinderTickers } from "../../../../../hooks";
import SectionNetworkActivity from "../../CommonComponents/Sections/SectionContents/SectionNetworkActivity";
import SectionInvestmentSummary from "../../CommonComponents/Sections/SectionContents/SectionInvestmentSummary";
import SectionKeyMetrics from "../../CommonComponents/Sections/SectionContents/SectionKeyMetrics";
import SectionFinancialSummary from "../../CommonComponents/Sections/SectionContents/SectionFinancialSummary";
import SectionFinancialsRevenue from "../../CommonComponents/Sections/SectionContents/SectionFinancialsRevenue";
import SectionCashFlowBurn from "../../CommonComponents/Sections/SectionContents/SectionCashFlowBurn";
import React from "react";

interface Props {
  collectionId: string;
  isLoading?: boolean;
}

export const CompanyDetailViewFinancialsTab: React.FC<Props> = ({ collectionId, isLoading = false }) => {
  const [tickerData, setTickerData] = React.useState<DealFinderTicker | null>(null);

  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });

  const { getDealFinderTickers } = useDealFinderTickers();

  const ticker = useCollectionKey(collectionId, "ticker");
  const exchange = useCollectionKey(collectionId, "stockExchange");

  // Fetch ticker data
  React.useEffect(() => {
    const fetchTickerData = async () => {
      if (ticker && exchange) {
        try {
          const data = await getDealFinderTickers({
            symbolWithExchange: `${exchange}:${ticker}`,
          });
          setTickerData(data.length > 0 ? data[0] : null);
        } catch (error) {
          console.error("Error fetching ticker data:", error);
          setTickerData(null);
        }
      }
    };

    fetchTickerData();
  }, [ticker, exchange, getDealFinderTickers]);

  // Mock investment summary data (would come from ticker data in real implementation)
  const investmentSummaryData = React.useMemo(() => {
    if (tickerData) {
      return {
        totalTransactions: 68,
        runningTotalRaised: "$7.4M",
        mostRecentValuation: "$32.6M",
        totalShares: "18,390,068",
        lastSharePrice: "$1.77",
        totalInvestors: 58,
      };
    }
    // Mock data when no real data available
    return {
      totalTransactions: 68,
      runningTotalRaised: "$7.4M",
      mostRecentValuation: "$32.6M",
      totalShares: "18,390,068",
      lastSharePrice: "$1.77",
      totalInvestors: 58,
    };
  }, [tickerData]);

  const keyInvestmentMetricsData = React.useMemo(() => {
    if (tickerData) {
      return {
        grossBurnRate: "-677,929",
        debtToEquityRatio: 5.2,
        cashPosition: "$0",
        currentRatio: 0.8,
        runway: "$0",
        netBurnRate: "$-336,999",
      };
    }
    // Mock data when no real data available
    return {
      grossBurnRate: "-677,929",
      debtToEquityRatio: 5.2,
      cashPosition: "$0",
      currentRatio: 0.8,
      runway: "$0",
      netBurnRate: "$-336,999",
    };
  }, [tickerData]);

  const financialsSummaryData = React.useMemo(() => {
    if (tickerData) {
      return {
        grossProfitMargin: "3%",
        quickRatio: 5,
        operatingCashFlowMargin: "-88%",
        operatingMargin: "0%",
        returnOnAssets: "15.9%",
        freeCashFlow: "$-643,083",
        netProfitMargin: "100%",
        returnOnEquity: "71.4%",
      };
    }
    // Mock data when no real data available
    return {
      grossProfitMargin: "3%",
      quickRatio: 5,
      operatingCashFlowMargin: "-88%",
      operatingMargin: "0%",
      returnOnAssets: "15.9%",
      freeCashFlow: "$-643,083",
      netProfitMargin: "100%",
      returnOnEquity: "71.4%",
    };
  }, [tickerData]);

  // Mock data for sections that can't be populated with real data
  const mockNetworkActivityData = [
    { date: "Day 1", searches: 45, views: 25, pins: 2 },
    { date: "Day 2", searches: 52, views: 28, pins: 3 },
    { date: "Day 3", searches: 48, views: 32, pins: 2 },
    { date: "Day 4", searches: 65, views: 35, pins: 4 },
    { date: "Day 5", searches: 58, views: 30, pins: 3 },
    { date: "Day 6", searches: 42, views: 22, pins: 1 },
    { date: "Day 7", searches: 55, views: 28, pins: 3 },
  ];

  const mockRevenueData = [
    { name: "2023 Q4", revenue: 856481 },
    { name: "2024 Q1", revenue: 923810 },
    { name: "2024 Q2", revenue: 749121 },
    { name: "2024 Q3", revenue: 1728354 },
  ];

  const mockCashFlowBurnData = [
    { name: "2023 Q4", cashFlow: 0, burnRate: 751834 },
    { name: "2024 Q1", cashFlow: 0, burnRate: 928022 },
    { name: "2024 Q2", cashFlow: 231671, burnRate: 1495837 },
    { name: "2024 Q3", cashFlow: 452092, burnRate: 1403358 },
  ];

  console.log("Is page loading: ", isLoading);

  return (
    <SectionContentLayout useGrid className="ch-company-dashboard-sections">
      {/* Left Column */}
      <Stack spacing={isMobile ? ".5rem" : "1rem"}>
        <SectionNetworkActivity networkActivityData={mockNetworkActivityData} />
        <SectionInvestmentSummary investmentSummaryData={investmentSummaryData} />
        <SectionKeyMetrics keyMetricsData={keyInvestmentMetricsData} />
      </Stack>

      {/* Right Column */}
      <Stack spacing={isMobile ? ".5rem" : "1rem"}>
        <SectionFinancialSummary financialSummaryData={financialsSummaryData} />
        <SectionFinancialsRevenue revenueData={mockRevenueData} />
        <SectionCashFlowBurn cashFlowBurnData={mockCashFlowBurnData} />
      </Stack>
    </SectionContentLayout>
  );
};
